import React, { useMemo, useState, useEffect } from 'react';

import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { withRouter, WithRouterProps } from 'react-router';
import { useApolloClient } from '@apollo/client';
import { isEmpty } from 'lodash';

import {
  Core_Ad_Banner_WrapperQuery,
  Core_Ad_Banner_WrapperQueryVariables,
  Core_Marketplace_Remixer_SearchQuery,
  Core_Marketplace_Remixer_SearchQueryVariables,
  MarketplaceQuerySuggestionsQuery,
  MarketplaceQuerySuggestionsQueryVariables,
} from '@reverbdotcom/commons/src/gql/graphql';

import {
  MARKETPLACE_REMIXER_SEARCH,
  v0Input,
} from './query_helpers';

import CombinedBumpListingGrid from './combined_bump_listing_grid';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import RQLPagination from '@reverbdotcom/commons/src/components/rql_pagination';
import { FavoriteButtonContainer } from '../../favorites/favorite_button_container';
import { FindFavoriteProvider } from '../../favorites/find_favorite_context';
import { getOffsetFromPage } from '../../../pagination_helpers';
import { QUERY_SUGGESTIONS_QUERY } from '../../with_query_suggestions';
import MarketplaceRelatedSearches from '../../discovery/related_searches';
import HeaderWithMetadata from '../header_with_metadata';
import { BumpDSACompliancePopover } from '@reverbdotcom/commons/src/components/bump_dsa_compliance_popover';
import { adBannerQuery } from '@reverbdotcom/commons/src/components/ads/ad_fragments';
import { parseKeywords } from '@reverbdotcom/commons/src/components/ads/ad_banner_helpers';
import { AdBanner } from '@reverbdotcom/commons/src/components/ads/ad_banner';
import SearchOverview from './search_overview';
import { getItem, setItem } from '@reverbdotcom/commons/src/local_storage_utils';
import { LIST_VIEW_STATE } from '@reverbdotcom/commons/src/components/list_view_toggle';
import { useSearchRedirects } from './marketplace_autodirects';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { MParticleEventName, MParticlePageName, trackPageView } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import ScrollTarget from '@reverbdotcom/commons/src/components/scroll_target';
import { trackingData } from './view_tracking_data';
import LowResultsBanner from './low_results_banner';

function MarketplacePageV2(props: WithRouterProps) {
  const apolloClient = useApolloClient();
  const url = useURL();
  const mappedInput = v0Input(url);
  const [listViewEnabled, setListViewEnabled] = useState(getItem(LIST_VIEW_STATE));
  const [scrollTarget, setScrollTarget] = useState<ScrollTarget>(null);

  const remixerSearchResults = useQuery<
    Core_Marketplace_Remixer_SearchQuery,
    Core_Marketplace_Remixer_SearchQueryVariables
  >(
    MARKETPLACE_REMIXER_SEARCH,
    {
      ssr: false,
      variables: {
        input: {
          searchRemixerV0Input: mappedInput,
        },
      },
    },
  );

  const marketplaceListingsSearchData = useMemo(() => {
    return remixerSearchResults.data ?? remixerSearchResults.previousData;
  }, [remixerSearchResults]);

  const { marketplaceListingsSearch } = marketplaceListingsSearchData ?? {};

  const {
    data: querySuggestionsResponse, loading: querySuggestionsLoading,
  } = useQuery<MarketplaceQuerySuggestionsQuery, MarketplaceQuerySuggestionsQueryVariables>(
    QUERY_SUGGESTIONS_QUERY,
    {
      ssr: false,
      variables: {
        searchQueries: url.query?.query ? [
          {
            query: url.query?.query || '',
          },
        ] : [],
      },
    },
  );

  const {
    data: adResponse, loading: adLoading,
  } = useQuery<Core_Ad_Banner_WrapperQuery, Core_Ad_Banner_WrapperQueryVariables>(
    adBannerQuery,
    {
      ssr: true,
      variables: { keywords: parseKeywords(url, url.query) },
    },
  );

  useSearchRedirects({
    apolloClient,
    marketplaceListingsSearch,
    url,
    routerProps: props,
  });

  useEffect(() => {
    if (!remixerSearchResults.loading) {
      trackPageView(trackingData(url, marketplaceListingsSearch, querySuggestionsResponse, listViewEnabled));
    }
  }, [marketplaceListingsSearch]);

  useEffect(() => {
    if (scrollTarget?.scrollToTop) {
      scrollTarget.scrollToTop();
    }
  }, [scrollTarget, url?.query?.page]);

  const { errors, loading } = remixerSearchResults;
  const listings = marketplaceListingsSearch?.listings;
  const promotionInfo = marketplaceListingsSearch?.promotionInfo;
  const queryInfo = marketplaceListingsSearch?.queryInfo;

  const pagination = marketplaceListingsSearch?.pagination || { total: 0, page: 1, pageSize: 45 };
  const offset = getOffsetFromPage(pagination.page.toString(), pagination.pageSize);

  const filters = marketplaceListingsSearch?.facetInfo;
  const facetGroups = filters?.facetGroups || [];

  const querySuggestions = querySuggestionsResponse?.querySuggestions?.queries?.[0]?.suggestions ?? [];

  if (marketplaceListingsSearch) { console.log('marketplace listings data', marketplaceListingsSearch); }

  function toggleListView() {
    setItem(LIST_VIEW_STATE, !listViewEnabled);
    setListViewEnabled(!listViewEnabled);
  }

  const renderFollowBanner = !(adLoading || isEmpty(marketplaceListingsSearch) || loading);
  const query = queryInfo?.executedQuery || url.query?.query || '';

  function tipText() {
    if (!queryInfo || !queryInfo.showOriginalQueryLink) return <BumpDSACompliancePopover />;

    const revertSearchUrl = Paths.marketplaceV2.expand({
      ...props.location.query,
      skip_autocorrect: true,
      query: queryInfo.originalQuery,
    });
    const clickEvent = {
      eventName: MParticleEventName.ClickedAutocorrectOriginalQuery,
      originalQuery: queryInfo.originalQuery,
      correctedQuery: queryInfo.executedQuery,
      componentName: MParticlePageName.Marketplace,
    };
    return (
      <div className="mb-3 tablet-mb-1">
        <div className="size-fixed-110">
          Search instead for
          <CoreLink
            to={revertSearchUrl}
            className="text-link ml-1"
            clickEvent={clickEvent}
          >
            {queryInfo.originalQuery}
          </CoreLink>
        </div>
      </div>
    );
  }

  const titleFollowText = queryInfo?.showOriginalQueryLink ? <BumpDSACompliancePopover /> : null;

  return (
    <FindFavoriteProvider>
      <div className="faceted-grid faceted-grid--wide">
        <div className="faceted-grid__inner">
          <div className="faceted-grid__main">
            <HeaderWithMetadata
              params={url.query}
              query={url.query?.query || ''}
            />
            <ScrollTarget onlyScrollUp ref={setScrollTarget} />
            {
              !adLoading && !!adResponse?.adServe?.ad?.imageAdCreative &&
              <div className="mt-6 mb-4 site-wrapper">
                <AdBanner
                  ad={adResponse.adServe.ad}
                />
              </div>
            }
            <MarketplaceRelatedSearches
              querySuggestions={querySuggestions}
              loading={querySuggestionsLoading}
              relatedSearchLocation="top"
              marketplacePageV2
              displayPills
            />
            <SearchOverview
              facetGroups={facetGroups}
              loading={loading}
              query={query}
              resultsTotal={pagination.total}
              sorts={marketplaceListingsSearch?.sorts}
              tipText={tipText()}
              toggleListView={toggleListView}
              titleFollowText={titleFollowText}
            />
            <LowResultsBanner queryInfo={queryInfo} />
            <CombinedBumpListingGrid
              error={!!errors?.length}
              isListView={listViewEnabled}
              loading={loading}
              listings={listings}
              promotionInfo={promotionInfo}
            />
            <RQLPagination
              limit={pagination.pageSize}
              total={pagination.total}
              offset={offset}
            />
            <MarketplaceRelatedSearches
              querySuggestions={querySuggestions}
              loading={querySuggestionsLoading}
              relatedSearchLocation="bottom"
              marketplacePageV2
            />
            {renderFollowBanner && <FavoriteButtonContainer displayStyle="banner" />}
          </div>
        </div>
      </div>
    </FindFavoriteProvider>
  );
}

export default withRouter(MarketplacePageV2);
